import { Controller } from "@hotwired/stimulus"

// A batch form in the Brand Data Management tool (Identify, or Parse & import). Sends the filters
// as a dry run to show what matches, then queues them once the count is confirmed.
export default class extends Controller {
  static targets = [
    "scope", "brandsWrapper", "brandSelect", "brandStatusSelect", "parseStatusSelect",
    "identityStatusSelect", "latestOnly", "previewButton", "runButton", "result"
  ]
  static values = { url: String }

  toggleScope() {
    this.brandsWrapperTarget.hidden = this.brandScope() !== "selected"
  }

  preview() {
    if (!this.validate()) return

    this.post(true, "Counting matching FDDs...").done((data) => {
      this.resultTarget.innerHTML = data.success ? this.renderPreview(data) : this.errorHtml(data.errors)
    })
  }

  // Always previews first, so nothing is queued without the user seeing how many FDDs it means.
  run() {
    if (!this.validate()) return

    this.post(true, "Counting matching FDDs...").done((data) => {
      if (!data.success) {
        this.resultTarget.innerHTML = this.errorHtml(data.errors)
        return
      }
      this.resultTarget.innerHTML = this.renderPreview(data)
      if (!data.count) return

      const brands = data.brand_count + " brand" + (data.brand_count === 1 ? "" : "s")
      if (!window.confirm("Queue " + data.count + " FDDs across " + brands + "?")) return

      this.post(false, "Queuing...").done((queued) => {
        this.resultTarget.innerHTML = queued.success
          ? '<div class="alert alert-success mb-0">' + this.escapeHtml(queued.message) + "</div>"
          : this.errorHtml(queued.errors)
      })
    })
  }

  post(dryRun, pendingMessage) {
    this.setBusy(true)
    this.resultTarget.innerHTML = '<div class="text-muted">' + pendingMessage + "</div>"

    const params = {
      brand_scope: this.brandScope(),
      brand_statuses: this.selectedValues(this.brandStatusSelectTarget),
      parse_statuses: this.selectedValues(this.parseStatusSelectTarget),
      identity_statuses: this.selectedValues(this.identityStatusSelectTarget),
      latest_only: this.latestOnlyTarget.checked,
      dry_run: dryRun
    }
    if (params.brand_scope === "selected") params.brand_ids = this.selectedValues(this.brandSelectTarget)

    return $.post(this.urlValue, params)
      .fail(() => { this.resultTarget.innerHTML = this.errorHtml("Something went wrong.") })
      .always(() => { this.setBusy(false) })
  }

  validate() {
    if (this.brandScope() === "selected" && !this.selectedValues(this.brandSelectTarget).length) {
      this.resultTarget.innerHTML = this.errorHtml("Choose at least one brand, or pick All brands.")
      return false
    }
    return true
  }

  renderPreview(data) {
    if (!data.count) return '<div class="alert alert-warning mb-0">No FDDs match those filters.</div>'

    let html = '<div class="alert alert-info mb-2">' + data.count + " FDDs across " + data.brand_count +
      " brands match.</div>"
    html += '<ul class="small mb-0">' + data.sample.map((label) => "<li>" + this.escapeHtml(label) + "</li>").join("")
    if (data.count > data.sample.length) html += '<li class="text-muted">…and ' + (data.count - data.sample.length) + " more</li>"
    return html + "</ul>"
  }

  brandScope() {
    const checked = this.scopeTargets.find((radio) => radio.checked)
    return checked ? checked.value : "all"
  }

  // TomSelect keeps the underlying <select> in sync, so its selected options are the truth.
  selectedValues(select) {
    return Array.from(select.selectedOptions).map((option) => option.value).filter(Boolean)
  }

  setBusy(busy) {
    this.previewButtonTarget.disabled = busy
    this.runButtonTarget.disabled = busy
  }

  errorHtml(message) {
    return '<div class="alert alert-danger mb-0">' + this.escapeHtml(message || "Something went wrong.") + "</div>"
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML
  }
}
